import React from 'react'

import styled from 'theme/styled-components'
import { MapBuilding } from '../types'
import useI18n from 'i18n/useI18n'

interface Props {
  buildings: TotemBuildingV6[]
  defaultBuilding?: string
  currentBuilding?: MapBuilding
  onBuildingSelected: (visioglobeBuildingId?: string) => void
}

const TotemBuildingSelector = ({ buildings, defaultBuilding, currentBuilding, onBuildingSelected }: Props) => {
  const i18n = useI18n()

  const currentEntries = React.useMemo(
    () =>
      buildings
        .find((building) => building.visioglobeBuildingId === currentBuilding?.id)
        ?.locations?.filter((location) => location.type === 'BUILDING') || [],
    [buildings, currentBuilding]
  )

  const hasMultiplesEntries = React.useMemo(() => currentEntries?.length > 1, [buildings, currentBuilding])

  return (
    <BuildingsContainer>
      {!!currentBuilding && (
        <>
          <BuildingName>{currentBuilding.name}</BuildingName>

          <BuildingInfos>
            {currentEntries.map((location, index) => (
              <BuildingAddress key={index}>
                {hasMultiplesEntries && i18n.t(`common.${location.name}`) + ' - '}
                {location.address}
              </BuildingAddress>
            ))}
            <NbFloors>{i18n.t('screens.cartography.totem.floors', { count: currentBuilding?.floors.length })}</NbFloors>
          </BuildingInfos>
        </>
      )}

      <BuildingButtons>
        {buildings.map((building) => (
          <BuildingButton
            key={building.id}
            selected={currentBuilding?.id === building.visioglobeBuildingId}
            onClick={() => onBuildingSelected(building.visioglobeBuildingId)}>
            {building.name}
            {building?.name === defaultBuilding && (
              <BuildingButtonText>{i18n.t('screens.cartography.totem.(youAreHere)')}</BuildingButtonText>
            )}
          </BuildingButton>
        ))}
      </BuildingButtons>
    </BuildingsContainer>
  )
}

export default TotemBuildingSelector

const BuildingsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  gap: 40px;
  padding: 0 40px;
`

const BuildingName = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  font-size: 100px;
  font-weight: 700;
  margin: 0;
`

const BuildingInfos = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const BuildingAddress = styled('p')`
  ${(props) => props.theme.fonts.totemLight};
  font-size: 44px;
  font-weight: 400;
  line-height: 44px;
  gap: 10px;
  margin: 0;
`

const NbFloors = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  font-size: 44px;
  font-weight: 400;
  line-height: 44px;
  margin: 20px 0 0 0;
`

const BuildingButtons = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 40px;
  margin: 10px 0;
`

const BuildingButton = styled('button')<{ selected?: boolean }>`
  ${(props) => props.theme.fonts.totemBigLight};
  background-color: ${(props) => (props.selected ? props.theme.colors.blue : props.theme.colors.white)};
  color: ${(props) => (props.selected ? props.theme.colors.white : props.theme.colors.black)};
  ${(props) => !props.selected && 'font-weight: 400;'}
  border: none;
  border-radius: 5px;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  padding: 30px 40px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  border-radius: 20px;
`

const BuildingButtonText = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  color: inherit;
  font-weight: 300;
  margin: 0;
`
